import React, { useState, useEffect, useMemo } from 'react';
import { 
  Trophy, Medal, Crown, MapPin, Users, Star, School, 
  ChevronLeft, Filter, ChevronDown, Award, Sparkles
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import BottomNav from '../components/BottomNav';
import api from '../services/api';
import { toast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';

const GirlsRoomLeaderboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('participants');
  const [selectedCity, setSelectedCity] = useState('all');
  const [cities, setCities] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [citiesStats, setCitiesStats] = useState([]);

  const activeCities = useMemo(() => citiesStats.filter(c => c.participantCount > 0), [citiesStats]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [citiesRes, leaderboardRes, citiesStatsRes] = await Promise.all([
        api.get('/cities'),
        api.get(`/girls-room/leaderboard${selectedCity !== 'all' ? `?city=${selectedCity}` : ''}`),
        api.get('/girls-room/leaderboard/by-city')
      ]);
      setCities(citiesRes.data.cities || []);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
      setCitiesStats(citiesStatsRes.data.cities || []);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger le classement',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity]);

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-300" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="text-pink-300 font-bold text-lg">#{rank}</span>;
  };

  const getRankBgColor = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-500/30 to-amber-500/30 border-yellow-400/50';
    if (rank === 2) return 'bg-gradient-to-r from-gray-400/20 to-gray-500/20 border-gray-400/50';
    if (rank === 3) return 'bg-gradient-to-r from-amber-600/20 to-orange-600/20 border-amber-600/50';
    return 'bg-white/5 border-white/10';
  };

  const getLevelStage = (level) => {
    if (level >= 11) return { emoji: '✨', name: 'Transcendance' };
    if (level >= 9) return { emoji: '🌻', name: 'Épanouissement' };
    if (level >= 7) return { emoji: '🌺', name: 'Floraison' };
    if (level >= 5) return { emoji: '🌸', name: 'Bourgeon' };
    if (level >= 3) return { emoji: '🌿', name: 'Pousse' };
    return { emoji: '🌱', name: 'Graine' };
  };

  const ParticipantCard = ({ participant, rank }) => {
    const stage = getLevelStage(participant.level);
    
    return (
      <Card 
        className={`p-4 ${getRankBgColor(rank)} border transition-all hover:scale-[1.02]`}
        data-testid={`participant-rank-${rank}`}
      >
        <div className="flex items-center gap-4">
          {/* Rank */}
          <div className="w-12 h-12 rounded-full flex items-center justify-center bg-white/10">
            {getRankIcon(rank)}
          </div>
          
          {/* Info */}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-white font-semibold">{participant.name}</span>
              <span className="text-lg">{stage.emoji}</span>
              {participant.hasBadge && (
                <span title="Ambassadrice Girls Room" className="text-pink-400">🌸</span>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-pink-300">{stage.name}</span>
              {participant.city && (
                <span className="text-pink-400 text-xs flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {participant.city}
                </span>
              )}
            </div>
          </div>
          
          {/* Stats */}
          <div className="text-right">
            <div className="text-yellow-400 font-bold text-lg">{participant.points.toLocaleString()}</div>
            <div className="text-pink-300 text-xs">points</div>
            <div className="text-cyan-400 text-sm font-medium">Niv. {participant.level}</div>
          </div>
        </div>
        
        {/* Attendance badges */}
        {participant.girlsRoomAttendances > 0 && (
          <div className="mt-2 pt-2 border-t border-white/10 flex items-center gap-2">
            <School className="w-4 h-4 text-pink-400" />
            <span className="text-pink-200 text-xs">
              {participant.girlsRoomAttendances} participation(s) Girls Room
            </span>
          </div>
        )}
      </Card>
    );
  };

  const CityCard = ({ city, rank }) => {
    return (
      <Card 
        className={`p-4 ${getRankBgColor(rank)} border transition-all hover:scale-[1.02]`}
        data-testid={`city-rank-${rank}`}
      >
        <div className="flex items-center gap-4">
          {/* Rank */}
          <div className="w-12 h-12 rounded-full flex items-center justify-center bg-white/10">
            {getRankIcon(rank)}
          </div>
          
          {/* City Info */}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-pink-300" />
              <span className="text-white font-semibold">{city.city}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-pink-300">
              <Users className="w-3 h-3" />
              <span>{city.participantCount} participantes</span>
            </div>
          </div>
          
          {/* Stats */}
          <div className="text-right">
            <div className="text-yellow-400 font-bold text-lg">{city.totalPoints.toLocaleString()}</div>
            <div className="text-pink-300 text-xs">points totaux</div>
            <div className="text-cyan-400 text-sm">
              Moy: {city.avgPoints} pts
            </div>
          </div>
        </div>
        
        {/* Top user */}
        {city.topUser && city.topUser.name && (
          <div className="mt-2 pt-2 border-t border-white/10 flex items-center gap-2">
            <Crown className="w-4 h-4 text-yellow-400" />
            <span className="text-pink-200 text-xs">
              Top: {city.topUser.name} ({city.topUser.points} pts)
            </span>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-24">
      {/* Header */}
      <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 z-40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/10 rounded-lg">
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <div className="flex items-center gap-2">
              <Trophy className="w-6 h-6 text-yellow-400" />
              <h1 className="text-xl font-bold text-white">Classement Girls Room</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
        {/* Hero */}
        <Card className="p-6 bg-gradient-to-br from-pink-500/30 to-purple-600/30 backdrop-blur-md border-pink-400/30">
          <div className="text-center">
            <div className="flex justify-center gap-2 mb-4">
              <Star className="w-8 h-8 text-yellow-400" />
              <span className="text-5xl">🌸</span>
              <Star className="w-8 h-8 text-yellow-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Girls Room Champions</h2>
            <p className="text-pink-100 text-sm">
              Découvrez les participantes les plus actives des sessions Girls Room!
            </p>
          </div>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-white/10">
            <TabsTrigger 
              value="participants" 
              className="data-[state=active]:bg-pink-500/50"
              data-testid="tab-participants"
            >
              <Users className="w-4 h-4 mr-2" />
              Participantes
            </TabsTrigger>
            <TabsTrigger 
              value="cities"
              className="data-[state=active]:bg-pink-500/50"
              data-testid="tab-cities"
            >
              <MapPin className="w-4 h-4 mr-2" />
              Par ville
            </TabsTrigger>
          </TabsList>

          {/* Participants Tab */}
          <TabsContent value="participants" className="mt-4">
            {/* City Filter */}
            <div className="mb-4 flex items-center gap-2">
              <Filter className="w-4 h-4 text-pink-300" />
              <Select value={selectedCity} onValueChange={setSelectedCity}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white w-48">
                  <SelectValue placeholder="Filtrer par ville" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les villes</SelectItem>
                  {cities.map((city) => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Card key={i} className="p-4 bg-white/5 border-white/10 animate-pulse">
                    <div className="h-12 bg-white/10 rounded" />
                  </Card>
                ))}
              </div>
            ) : leaderboard.length > 0 ? (
              <div className="space-y-3">
                {leaderboard.map((participant, index) => (
                  <ParticipantCard key={participant.id} participant={participant} rank={index + 1} />
                ))}
              </div>
            ) : (
              <Card className="p-8 bg-white/10 border-white/20 text-center">
                <Users className="w-12 h-12 text-pink-300 mx-auto mb-4" />
                <h3 className="text-white font-semibold mb-2">Pas encore de participantes</h3>
                <p className="text-pink-200 text-sm">
                  {selectedCity !== 'all' 
                    ? `Aucune participante à ${selectedCity} pour le moment`
                    : 'Participez à une session Girls Room pour apparaître ici!'}
                </p>
              </Card>
            )}
          </TabsContent>

          {/* Cities Tab */}
          <TabsContent value="cities" className="mt-4">
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Card key={i} className="p-4 bg-white/5 border-white/10 animate-pulse">
                    <div className="h-12 bg-white/10 rounded" />
                  </Card>
                ))}
              </div>
            ) : activeCities.length > 0 ? (
              <div className="space-y-3">
                {activeCities.map((city, index) => (
                  <CityCard key={city.city} city={city} rank={index + 1} />
                ))}
              </div>
            ) : (
              <Card className="p-8 bg-white/10 border-white/20 text-center">
                <MapPin className="w-12 h-12 text-pink-300 mx-auto mb-4" />
                <h3 className="text-white font-semibold mb-2">Pas encore de données</h3>
                <p className="text-pink-200 text-sm">
                  Les statistiques par ville apparaîtront lorsque des participantes
                  rejoindront les sessions Girls Room!
                </p>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* How to earn points */}
        <Card className="p-4 bg-purple-500/10 border-purple-400/30">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-purple-300 mt-0.5" />
            <div>
              <h4 className="text-white font-medium mb-1">Comment grimper au classement?</h4>
              <ul className="text-purple-200 text-sm space-y-1">
                <li>• <span className="text-yellow-400">+50 pts</span> par leçon terminée</li>
                <li>• <span className="text-yellow-400">+100 pts</span> par quiz réussi</li>
                <li>• <span className="text-yellow-400">+150 pts</span> présence Girls Room confirmée</li>
                <li>• <span className="text-pink-400">🌸</span> Badge Ambassadrice après 3 présences</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>

      <BottomNav />
    </div>
  );
};

export default GirlsRoomLeaderboard;
